import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import DOMPurify from "dompurify";
import client from "@/api/client";
import { SEO } from "@/components/SEO";
import { JourneyCTA } from "@/components/JourneyCTA";
import { coverFor } from "@/lib/artwork";

/**
 * Public renderer for admin-created custom pages. Fetches from /api/pages/:slug.
 * 404s are handled with a friendly redirect to the NotFound page.
 *
 * Defensive rendering:
 *  - HTML is passed through DOMPurify before being injected via
 *    `dangerouslySetInnerHTML`. This is our XSS boundary: it strips
 *    <script>, on* handlers, javascript: URLs and any other unsafe
 *    constructs even if the backend sanitizer regressed.
 *  - Applies overflow-wrap/break-words CSS so pasted Word/Docs text can never
 *    overflow the container even if it contains long unbroken runs.
 *  - Client-side scrub for &nbsp; / U+00A0 as a safety net for legacy content
 *    served from caches that pre-date the backend sanitizer.
 */
function sanitizeContent(html) {
  if (!html) return "";
  let s = html;
  // Non-breaking spaces prevent wrapping — normalise them.
  s = s.replace(/&nbsp;|&#160;|&#xA0;/gi, " ").replace(/\u00A0/g, " ");
  // Strip inline styles that force widths / whitespace behaviour (Word paste)
  s = s.replace(
    /(?:white-space|word-break|overflow|width|max-width|min-width|font-family|line-height)\s*:\s*[^;"']+;?/gi,
    "",
  );
  s = s.replace(/\sstyle\s*=\s*(?:""|'')/gi, "");
  s = s.replace(/ {3,}/g, " ");
  // XSS scrub — strip <script>, event handlers, javascript: URLs, etc.
  return DOMPurify.sanitize(s, { USE_PROFILES: { html: true } });
}

/**
 * Split sanitized HTML into up to 3 roughly equal parts at top-level closing
 * block tags, so CTAs land between sections and never inside an element.
 */
function splitContent(html, parts = 3) {
  if (!html) return [];
  const boundary = /<\/(?:p|h2|h3|ul|ol|blockquote|table|figure)>/gi;
  const cuts = [];
  let m;
  while ((m = boundary.exec(html)) !== null) cuts.push(m.index + m[0].length);
  if (cuts.length < parts) return [html];
  const targets = Array.from({ length: parts - 1 }, (_, i) =>
    Math.round((html.length * (i + 1)) / parts),
  );
  const picked = [];
  targets.forEach((target) => {
    const at = cuts.reduce((best, c) =>
      (Math.abs(c - target) < Math.abs(best - target) && !picked.includes(c) ? c : best), cuts[0]);
    if (!picked.includes(at)) picked.push(at);
  });
  picked.sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  picked.forEach((at) => { out.push(html.slice(prev, at)); prev = at; });
  out.push(html.slice(prev));
  return out.filter((s) => s.trim());
}

export default function CustomPage() {  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    client
      .get(`/pages/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (alive) setPage(res.data);
      })
      .catch(() => {
        // Keep the requested path so the 404 page can name it.
        if (alive) navigate("/404", { replace: true, state: { from: `/p/${slug}` } });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug, navigate]);

  const isAr = i18n.language === "ar";
  const title = page ? (isAr ? page.title_ar : page.title_en) || page.title_en : "";
  const rawHtml = page ? (isAr ? page.content_ar : page.content_en) || page.content_en : "";
  const meta = page ? (isAr ? page.meta_desc_ar : page.meta_desc_en) || page.meta_desc_en : "";
  const cleanHtml = useMemo(() => sanitizeContent(rawHtml), [rawHtml]);
  // Split the article at top-level block boundaries so lead CTAs can sit
  // inside the content instead of only at the end.
  const segments = useMemo(() => splitContent(cleanHtml), [cleanHtml]);
  const cover = page ? coverFor(page.slug, page.cover_url) : "";
  // Many CMS pages have no meta description — derive one from the body so the
  // snippet never falls back to the homepage copy.
  const plainExcerpt = useMemo(() => {
    const src = (isAr && page?.excerpt_ar) || page?.excerpt_en || "";
    if (src) return src;
    return cleanHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 300);
  }, [page, isAr, cleanHtml]);

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="custom-page-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!page) return null;

  return (
    <>
      <SEO
        title={title}
        description={meta || page.excerpt_en || plainExcerpt}
        path={`/p/${page.slug}`}
        type={page.is_post ? "article" : "website"}
        image={cover}
        publishedTime={page.is_post ? page.created_at : undefined}
        modifiedTime={page.is_post ? page.updated_at : undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": page.is_post ? "BlogPosting" : "WebPage",
          headline: title,
          description: meta || plainExcerpt,
          image: cover,
          datePublished: page.created_at,
          dateModified: page.updated_at || page.created_at,
          inLanguage: isAr ? "ar" : "en",
          author: { "@type": "Organization", name: "The Saudi Gate" },
          publisher: { "@type": "Organization", name: "The Saudi Gate" },
        }}
      />
      <div className="relative">
        {/* Brand artwork backdrop so every page has a real background */}
        <div className="absolute inset-x-0 top-0 h-[360px] -z-10 overflow-hidden" aria-hidden>
          <img src={cover} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
        </div>

        <article
          className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20 break-words"
          data-testid="custom-page"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            data-testid="custom-page-back"
          >
            {isAr ? (
              <>
                {t("nav.back", { defaultValue: "العودة" })}
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                <ArrowLeft className="h-4 w-4" />
                {t("nav.back", { defaultValue: "Back" })}
              </>
            )}
          </Link>

          <header className="mb-10">
            <span className="section-eyebrow">The Saudi Gate</span>
            <h1
              className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-balance break-words"
              data-testid="custom-page-title"
            >
              {title}
            </h1>
            {meta && (
              <p
                className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed break-words"
                data-testid="custom-page-lede"
              >
                {meta}
              </p>
            )}
            <div className="mt-8 h-px w-16 bg-primary/60" />
          </header>

          {/*
            XSS boundary: `cleanHtml` was passed through DOMPurify.sanitize
            in `sanitizeContent()` above before hitting the DOM. Do NOT
            bypass that helper — every code path that renders CMS HTML
            must go through it. `segments` only slices that sanitized string.
          */}
          {(segments.length ? segments : [""]).map((seg, i) => (
            <div key={i}>
              {/* eslint-disable-next-line react/no-danger */}
              <div
                className="cms-content prose prose-slate dark:prose-invert max-w-none text-foreground leading-relaxed break-words [word-break:break-word] [overflow-wrap:anywhere] [&_*]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_a]:text-primary [&_a]:underline [&_a]:break-all [&_h2]:font-display [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h3]:font-display [&_h3]:mt-8 [&_h3]:mb-2 [&_h3]:text-xl [&_h3]:sm:text-2xl [&_ul]:list-disc [&_ul]:ps-6 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:ps-6 [&_ol]:my-4 [&_li]:my-1 [&_p]:my-4 [&_blockquote]:border-s-4 [&_blockquote]:border-primary/40 [&_blockquote]:ps-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_pre]:whitespace-pre-wrap [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded-md [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_table]:block [&_table]:overflow-x-auto [&_table]:w-full"
                dangerouslySetInnerHTML={{ __html: seg || "" }}
                data-testid={i === 0 ? "custom-page-content" : `custom-page-content-${i}`}
              />
              {i === 0 && segments.length > 1 && <JourneyCTA variant="bar" index={1} />}
              {i === 1 && segments.length > 2 && <JourneyCTA variant="card" index={2} />}
            </div>
          ))}

          <JourneyCTA variant="block" index={3} />
        </article>
      </div>
    </>
  );
}
